import { AlertBannerContainer } from "@/components/alert-banner";
import { DeprecatedRouteBanner } from "@/components/deprecated-view-banner";
import { MainMenu } from "@/components/main-menu";
import { MainMenu2 } from "@/components/main-menu-2";
import { FromNotification } from "@/components/notification/from-notification";
import { NotificationPopover } from "@/components/notification/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Toaster } from "@/components/ui/sonner";
import { UtilityBar } from "@/components/utility-bar";
import { ViewAsBanner } from "@/components/view-as-banner";
import { rememberWalterIaOpenRequest } from "@/components/walter-assistant/open-request";
import { LayoutProvider, useLayoutContext } from "@/contexts/layout-context";
import { SearchProvider, useSearchContext } from "@/contexts/search-context";
import { useSearchShortcut } from "@/hooks/use-keyboard-shortcut";
import { useActivityTracker } from "@/hooks/use-activity-tracker";
import { HOME_ENTRY, LAYOUT_AUDIENCES, useLandingHref, useMenuEntryAccess } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { SmsProvider } from "@/providers/sms";
import { useUser } from "@/providers/user";
import { WalterCoachProvider } from "@/providers/walter-coach";
import { useDebounce } from "@/utils/misc";
import { ChartNoAxesCombined, LayoutList, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, ScrollRestoration, useLocation, useNavigation } from "react-router-dom";

// Composant interne pour gérer les raccourcis clavier
function AppContent() {
    // Read on the first render, before the SSO or landing redirects rewrite the URL.
    useState(rememberWalterIaOpenRequest);
    const { user, has_any_group, audience, is_visible_to_audience } = useUser();
    const canSee = useMenuEntryAccess();
    const { authenticated, loading, viewAsActive } = useAuth();
    const location = useLocation();
    const navigation = useNavigation();
    const debouncedNavigationState = useDebounce(navigation.state, 300, state => state != "idle");
    const apprenticeship_teams = has_any_group(["app_apprenticeship_admin", "team_apprenticeship_cap", "team_apprenticeship_ce", "team_apprenticeship_csm"]);

    // Récupérer la fonction pour focus sur la recherche
    const { focusSearch } = useSearchContext();

    // Par défaut, le nouveau menu est affiché, l'ancien menu s'affiche si localStorage est "true"
    const [useLegacyMenu, setUseLegacyMenu] = useState(() => {
        if (typeof window === "undefined") return true;
        const value = localStorage.getItem("use_legacy_menu");
        return value === null ? false : value === "true";
    });

    useEffect(() => {
        // On prend la valeur du localStorage avec fallback sur true par défaut
        const handler = () => {
            const value = localStorage.getItem("use_legacy_menu");
            setUseLegacyMenu(value === null ? false : value === "true");
        };
        window.addEventListener("storage", handler);
        window.addEventListener("legacy-menu-changed", handler);
        // Met à jour immédiatement
        const value = localStorage.getItem("use_legacy_menu");
        setUseLegacyMenu(value === null ? false : value === "true");
        return () => {
            window.removeEventListener("storage", handler);
            window.removeEventListener("legacy-menu-changed", handler);
        };
    }, []);

    // Raccourci clavier universel pour CMD+K (Mac) et Ctrl+K (Windows/Linux)
    useSearchShortcut(() => {
        focusSearch();
    });
    useActivityTracker(authenticated && is_visible_to_audience(LAYOUT_AUDIENCES.activityTracking));
    // The legacy menu is an opt-in of standard users (shown as before while the user loads).
    const showLegacyMenu = useLegacyMenu && (audience === null || is_visible_to_audience(LAYOUT_AUDIENCES.legacyMenu));
    const showUtilityBar = is_visible_to_audience(LAYOUT_AUDIENCES.utilityBar);
    // A user who may not use the home dashboard lands on their first menu entry instead.
    const redirectHome = location.pathname === HOME_ENTRY.href && audience !== null && !canSee(HOME_ENTRY);

    // Utiliser le contexte pour déterminer si on affiche l'espace pour UtilityBar
    const { showUtilityBarSpace } = useLayoutContext();

    if (loading) {
        return <div>Loading</div>;
    }

    if (!authenticated) {
        return <Navigate to="/connexion/sso" state={{ from: location }} replace />;
    }

    return (
        <>
            {/* Keyed on pathname so filters, tabs and pagination held in the query string keep the scroll position. */}
            <ScrollRestoration getKey={location => location.pathname} />
            <div className="relative flex min-h-screen flex-col bg-gray-100">
                {showLegacyMenu ? (
                    <div className="sticky top-0 z-50">
                        <ViewAsBanner />
                        <div className="flex justify-between border-b border-slate-100 bg-white px-4 py-2">
                            <MainMenu />
                            <div className="flex items-center space-x-3">
                                {has_any_group(["app_users_admin"]) && (
                                    <NavLink to="/objectif/mensuel" className="rounded-full p-2 text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700">
                                        <ChartNoAxesCombined className="text-black" size={20} />
                                    </NavLink>
                                )}
                                {apprenticeship_teams && (
                                    <NavLink to="/activites" className="rounded-full p-2 text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700">
                                        <LayoutList className="text-black" size={20} />
                                    </NavLink>
                                )}
                                {user?.is_super_admin || has_any_group(["team_apprenticeship_cap", "team_apprenticeship_ce", "team_apprenticeship_csm", "team_apprenticeship_sales"]) ? (
                                    <NotificationPopover />
                                ) : null}
                                <NavLink to="/parametres" className="rounded-full p-2 text-slate-500 transition-all hover:bg-slate-100 hover:text-slate-700">
                                    <Settings className="text-black" size={20} />
                                </NavLink>
                                <Avatar>
                                    <AvatarFallback>{user ? user.first_name[0] : "X"}</AvatarFallback>
                                </Avatar>
                            </div>
                        </div>
                    </div>
                ) : (
                    <MainMenu2 />
                )}
                <div className={cn("flex-1", !showLegacyMenu && (viewAsActive ? "mt-[120px]" : "mt-[87px]"))}>
                    <div
                        className={cn("p-4", debouncedNavigationState == "loading" ? "opacity-60" : "")}
                        style={{
                            backgroundColor: "#f3F4F6",
                            backgroundImage: "url('/background.png')",
                            backgroundSize: "contain",
                            backgroundPosition: "top ",
                            backgroundRepeat: "no-repeat"
                            // backgroundBlendMode: "overlay"
                        }}
                    >
                        <DeprecatedRouteBanner />
                        {redirectHome ? <LandingRedirect /> : <Outlet />}
                    </div>
                </div>
            </div>

            <AlertBannerContainer />
            <Toaster />
            {showUtilityBar && showUtilityBarSpace && <div className="h-11" />}
            {showUtilityBar && <UtilityBar />}
            <FromNotification />
        </>
    );
}

/** In place of the home dashboard: the user's first menu entry (see `useLandingHref`). */
function LandingRedirect() {
    const landing = useLandingHref();
    if (landing === undefined) return null;
    if (landing === null || landing === HOME_ENTRY.href) {
        return <div className="text-sm text-gray-500">Aucun espace n'est encore ouvert pour votre compte.</div>;
    }
    return <Navigate to={landing} replace />;
}

export default function AppLayout() {
    return (
        <SearchProvider>
            <LayoutProvider>
                <SmsProvider>
                    <WalterCoachProvider>
                        <AppContent />
                    </WalterCoachProvider>
                </SmsProvider>
            </LayoutProvider>
        </SearchProvider>
    );
}
